import { KeyValue } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal
} from '@angular/core';
import {
  CheckboxNode,
  CheckboxesComponent,
  ReactiveCheckboxWatcherService,
  ToolkitApiService
} from '@jeppesen-foreflight/jmkw-toolkit';
import { ExtendedMap } from '@jeppesen-foreflight/jmkw-types';
import { NGXLogger } from 'ngx-logger';
import { NavigationLayer } from '../../../layer/navigation.layer';
import { NavigationPseudoLayer } from '../../../layer/pseudo-layer/navigation.pseudo-layer';
import { convertPseudoLayersToCheckboxNodeMap } from '../../../layer/pseudo-layer/pseudo-layer.utils';

@Component({
  selector: 'jmkw-navigation-popout',
  standalone: true,
  imports: [CheckboxesComponent],
  templateUrl: './navigation-popout.component.html',
  styleUrls: ['./navigation-popout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NavigationPopoutComponent implements OnInit {
  private readonly _api = inject(ToolkitApiService);
  private readonly _logger = inject(NGXLogger);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _reactiveCheckboxWatcher = inject(
    ReactiveCheckboxWatcherService
  );

  private _navLayer?: NavigationLayer;

  automationKey = 'navigation-popout';

  checkboxNodeMap = signal<ExtendedMap<CheckboxNode>>(new ExtendedMap());

  ngOnInit() {
    // Try to get the layer and log more information
    this._navLayer = this._api.getLayerById<NavigationLayer>('navigation');

    if (!this._navLayer) {
      console.error('Navigation layer not found', {
        availableLayers: this._api.getMapOfLayers().keys(), // Assuming such a method exists
        layers: this._api.getMap().allLayers // Another hypothetical method to get all layers
      });
      return;
    }

    // Set initial checkbox map
    this.updateCheckboxNodeMap();

    // Set up reactive monitoring for layer visibility changes
    this.setupLayerVisibilityWatchers();
  }

  handleCheckboxChange({ key, value }: KeyValue<string, CheckboxNode>) {
    const layer = this._api.findLayer<NavigationPseudoLayer>({
      id: key
    }).layer as NavigationPseudoLayer;

    if (layer) {
      layer.visible = value.selected;
    } else {
      console.warn(`Layer ${key} not found`);
    }

    if (value.children?.size) {
      value.children.forEach((value: CheckboxNode, key: string) => {
        this.handleCheckboxChange({ key, value });
      });
    }
  }

  private setupLayerVisibilityWatchers() {
    if (!this._navLayer) return;

    this._reactiveCheckboxWatcher.setupWatchers(
      this._navLayer.layers,
      (pseudoLayer: any) => pseudoLayer.visible,
      (pseudoLayer: any) => pseudoLayer.layers, // Handle nested layers
      () => {
        this.updateCheckboxNodeMap();
      },
      this._destroyRef
    );
  }

  private updateCheckboxNodeMap() {
    if (!this._navLayer) {
      console.error('No navigation layer available');
      this.checkboxNodeMap.set(new ExtendedMap());
      return;
    }

    const pseudoLayers = this._navLayer.layers;
    const newCheckboxMap = convertPseudoLayersToCheckboxNodeMap(
      pseudoLayers,
      true
    );
    this.checkboxNodeMap.set(newCheckboxMap);
  }
}
